/* ============================================================================
   mz-dawn-bridge.js — utilities the ported Dawn components (cart-drawer,
   details-modal search, predictive search) need but this theme doesn't define.
   Loaded in <head> (defer) BEFORE cart-drawer.js / details-modal.js /
   search-form.js so classes find these at define/upgrade time.

   Also owns two site-wide behaviors:
   • Rebinds the theme's header search triggers (.top-search-toggle) to the new
     Dawn-style search modal (details-modal.header__search).
   • mzEmit(): one custom-event helper so every ported feature announces itself
     (cart:updated, cart:item-added, cart-drawer:open/close, search:open/close,
     mbb:*, cart-dock:*) for apps/scripts to hook into.
   ========================================================================== */

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;
    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return;
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  if (elementToFocus) elementToFocus.focus();

  if (
    elementToFocus &&
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);
  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;
  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;
  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

/* The theme defines these too (theme.js), but that loads at the END of <body>;
   the ported components upgrade earlier, so guarantee them here. Same semantics. */
if (typeof window.debounce !== 'function') {
  window.debounce = function (fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };
}
if (typeof window.fetchConfig !== 'function') {
  window.fetchConfig = function (type = 'json') {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
    };
  };
}

/* ---- Custom events on all fronts ------------------------------------------
   mzEmit('cart:updated', {...}) → document CustomEvent, bubbles, detail payload.
   Every ported feature calls this so storefront scripts and apps can listen:
     document.addEventListener('cart:item-added', (e) => console.log(e.detail))
   ------------------------------------------------------------------------- */
window.mzEmit = function (name, detail) {
  try {
    document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail || {} }));
  } catch (e) {}
};

/* ---- Search upgrade: route ALL search triggers to the new modal ----------
   The theme's header renders .top-search-toggle buttons wired (in theme.js) to
   the old popup/drawer search. Capture-phase interception opens the new
   Dawn-style predictive modal instead — theme handlers never fire. */
(function () {
  function searchModal() {
    return document.querySelector('details-modal.header__search');
  }
  document.addEventListener(
    'click',
    function (e) {
      var trigger = e.target.closest && e.target.closest('.top-search-toggle, [data-open-search-modal]');
      if (!trigger) return;
      var modal = searchModal();
      if (!modal) return; // modal not on page — let the theme handle it
      e.preventDefault();
      e.stopImmediatePropagation();
      if (typeof modal.openModal === 'function') {
        modal.openModal(trigger);
      } else {
        var details = modal.querySelector('details');
        if (details && !details.hasAttribute('open')) details.setAttribute('open', '');
        var input = modal.querySelector('input[type="search"]');
        if (input) setTimeout(function () { input.focus(); }, 60);
      }
    },
    true
  );
})();

/* Out-of-stock reordering REMOVED (Aug 2026): a collection-sorting app owns
   product order now. The gray OOS stamp/label/disarm styling is separate and stays. */

